import { ScanText } from 'lucide-react';
import type { Dimension, JDExtraction } from '@/lib/assessment';
import { DIMENSION_META } from '@/lib/assessment';

type Props = {
  extraction: JDExtraction;
};

type Section = {
  dim: Dimension;
  /** What this aspect IS (the JD content). */
  title: string;
  /** Why we assess it / what it tells the user about the funnel step. */
  explain: string;
  /** Which questions it generated (the basis hint). */
  hint: string;
  text: string;
};

/**
 * Renders the three JD aspects extracted at quiz-generation time — the basis
 * for each assessment dimension's questions. Shown on the job detail page so
 * users understand both the job (a cleaner breakdown than the raw JD) and why
 * each question was asked. Each card carries an explanation of the application-
 * funnel step it maps to (willingness → ATS screening → interview), so users
 * grasp why all three matter and why honest self-assessment pays off.
 */
export function JDBreakdown({ extraction }: Props) {
  const zh = extraction.language.toLowerCase().startsWith('zh');

  const intro = zh
    ? '我们从 JD 中提取了三个方面，对应「投递漏斗」三步自我评估：① 是否值得投递；② 简历能否通过 ATS 筛选；③ 面试能否通过。如实作答，才能客观判断这份工作是否真的适合你。'
    : 'We extracted three aspects from the JD, mapping to the three steps of the application funnel: ① whether to apply; ② whether your resume clears ATS screening; ③ whether you’d pass the interview. Answer honestly to judge if this role truly fits you.';

  const sections: Section[] = [
    {
      dim: 'willingness',
      title: zh ? '工作内容（岗位职责）' : 'Duties & responsibilities',
      explain: zh
        ? '明确这份工作每天到底做什么——是你判断「我是否真的愿意接受这份工作」的基础。'
        : 'What the day-to-day actually involves — the basis for judging whether you truly want to accept this job.',
      hint: zh ? '求职意愿题依据' : 'Basis for willingness questions',
      text: extraction.jobDescription,
    },
    {
      dim: 'hard_requirements',
      title: zh ? '硬性要求' : 'Hard requirements',
      explain: zh
        ? '当你基于客观信息（技能、经验、学历等）准备好并投递简历后，评估你的申请能否通过 ATS（申请跟踪系统）的自动化筛选、获得面试机会。'
        : 'After you submit a resume built on objective info (skills, experience, education), this gauges whether your application passes ATS (Applicant Tracking System) automated screening and earns an interview.',
      hint: zh ? '硬性要求题依据' : 'Basis for hard-requirement questions',
      text: extraction.hardRequirements,
    },
    {
      dim: 'soft_requirements',
      title: zh ? '柔性要求' : 'Soft requirements',
      explain: zh
        ? '当你成功获得面试机会后，初步评估你能否顺利通过面试、拿到 offer。'
        : 'Once you land an interview, this is a preliminary read on whether you can pass it and secure the offer.',
      hint: zh ? '柔性要求题依据' : 'Basis for soft-requirement questions',
      text: extraction.softRequirements,
    },
  ];

  return (
    <div className="mt-5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <ScanText className="h-3.5 w-3.5" />
          {zh ? 'JD 拆解' : 'JD breakdown'}
        </h2>
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {zh ? '评估问题的出题依据' : 'basis for your assessment questions'}
        </span>
      </div>

      <p className="mb-3 rounded-lg border border-primary/15 bg-primary/5 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
        {intro}
      </p>

      <div className="space-y-2.5">
        {sections.map((s) => {
          const Icon = DIMENSION_META[s.dim].icon;
          return (
            <div key={s.dim} className="rounded-lg border border-border bg-card p-3.5">
              <div className="flex items-center gap-1.5">
                <Icon className="h-4 w-4 text-primary" strokeWidth={2} />
                <p className="text-sm font-semibold text-foreground">{s.title}</p>
              </div>

              {/* Why this aspect is assessed — the funnel-step explanation. */}
              <div className={`mt-1.5 border-l-2 border-primary/25 pl-2 text-[12px] leading-snug ${DIMENSION_META[s.dim].accent}`}>
                {s.explain}
              </div>

              {s.text ? (
                <p className="mt-2.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                  {s.text}
                </p>
              ) : (
                <p className="mt-2.5 text-xs italic text-muted-foreground">
                  {zh ? '（JD 未明确）' : '(not stated in JD)'}
                </p>
              )}

              <p className="mt-2.5 border-t border-border/60 pt-1.5 text-[11px] font-medium text-muted-foreground">
                → {s.hint}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
